import { useRef, useEffect, useState } from 'react';
import { colors } from '../styles/colors';

/**
 * Composant de navigation par onglets avec animation de défilement de l'indicateur
 *
 * @param {Array} tabs - Tableau d'onglets avec { id, label, icon }
 * @param {string} activeTab - ID de l'onglet actif
 * @param {Function} onTabChange - Callback lors du changement d'onglet
 */
function TabNavigation({ tabs = [], activeTab, onTabChange }) {
  const tabRefs = useRef({});
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });

  // Mettre à jour la position de l'indicateur lors du changement d'onglet
  useEffect(() => {
    const activeTabElement = tabRefs.current[activeTab];
    if (activeTabElement) {
      const { offsetLeft, offsetWidth } = activeTabElement;
      setIndicatorStyle({
        left: offsetLeft,
        width: offsetWidth
      });
    }
  }, [activeTab]);

  const styles = {
    tabsContainer: {
      display: 'flex',
      gap: '0.5rem',
      borderBottom: `2px solid ${colors.border.light}`,
      marginBottom: '2rem',
      padding: '0 2rem',
      position: 'relative',
    },
    tab: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.5rem',
      border: 'none',
      backgroundColor: 'transparent',
      fontSize: '1rem',
      fontWeight: '500',
      fontFamily: '"Noto Sans", sans-serif',
      cursor: 'pointer',
      position: 'relative',
      transition: 'color 200ms',
      outline: 'none',
    },
    tabActive: {
      color: colors.primary.teal,
    },
    tabInactive: {
      color: colors.text.secondary,
    },
    tabIndicator: {
      position: 'absolute',
      bottom: '-2px',
      left: indicatorStyle.left,
      width: indicatorStyle.width,
      height: '2px',
      backgroundColor: colors.primary.teal,
      transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
      pointerEvents: 'none',
    },
  };

  return (
    <div style={styles.tabsContainer}>
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        const Icon = tab.icon;

        return (
          <button
            key={tab.id}
            ref={(el) => (tabRefs.current[tab.id] = el)}
            onClick={() => onTabChange(tab.id)}
            style={{
              ...styles.tab,
              ...(isActive ? styles.tabActive : styles.tabInactive),
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.color = colors.primary.tealDark;
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.color = colors.text.secondary;
              }
            }}
          >
            {Icon && <Icon size={20} />}
            <span>{tab.label}</span>
          </button>
        );
      })}
      {/* Indicateur animé unique */}
      <div style={styles.tabIndicator} />
    </div>
  );
}

export default TabNavigation;
